import { FC, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  repairFieldConfiguration,
  repairInitialValues,
} from "../../../formConfigs/Fleet/repairFieldConfig";
import {
  repairShopFieldConfiguration,
  repairShopInitialValues,
} from "../../../formConfigs/Fleet/repairShopFieldConfig";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { Button } from "@mui/material";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import { RepairPopupProps } from "../../../interfaces/Fleet/RepairPopupProps";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const RepairPopup: FC<RepairPopupProps> = ({
  isOpened,
  setIsOpened,
  incident,
  affectedTruckId,
  affectedTrailerId,
}) => {
  const [isRepairShopOpened, setIsRepairShopOpened] = useState(false);
  const [isRepairFormOpened, setIsRepairFormOpened] = useState(false);
  const [repairShopId, setRepairShopId] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [repairShopData, setRepairShopData] = useState<OptionType[]>([]);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const handleSnackbarClose = () => setStatus("idle");

  useEffect(() => {
    if (isOpened) {
      axios
        .get(`${apiVariable}/getAllRepairShops`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const formattedData = response.data.map((shop: any) => ({
            key: shop.id,
            label:
              shop.shopName + "—" + shop.streetAddress + "—" + shop.phoneNumber,
            id: shop.id,
          }));
          setRepairShopData(formattedData);
        })
        .catch((error) => {
          setStatus("error");
          setErrorMessage(
            error.response.data || "Failed to fetch repair shops"
          );
        });
    }
  }, [isOpened, token]);

  const repairShopMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/repairShop`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: (data) => {
      const newRepairShopId = data.data.id;
      setRepairShopId(newRepairShopId);
      setIsRepairShopOpened(false);
      setIsRepairFormOpened(true);
      setStatus("success");
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit repair shop");
    },
  });

  const repairMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/repair`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setIsRepairFormOpened(false);
      queryClient.invalidateQueries({ queryKey: ["repairs", incident.id] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      setStatus("success");
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit repair");
    },
  });

  const submitRepairShopHandler = (formData: any) => {
    const fullData = {
      ...formData,
    };
    repairShopMutation.mutate(fullData);
  };

  const submitRepairHandler = (formData: any) => {
    if (!formData.paymentMethod) {
      setStatus("error");
      setErrorMessage("Payment method is required");
      return;
    }

    const fullData = {
      ...formData,
      incidentId: incident.id,
      repairShopId: getId(formData.repairShopId) || getId(repairShopId),
      truckId: affectedTruckId,
      trailerId: affectedTrailerId,
    };
    repairMutation.mutate(fullData);
  };

  return (
    <>
      <GenericFormPopup
        formFields={[
          {
            name: "repairShopId",
            label: "Repair Shop",
            type: "combo",
            dataKey: "repairShops",
            validation: {
              required: "Repair shop is required",
            },
          },
        ]}
        initialValues={{ repairShopId: "" }}
        title="Select Repair Shop"
        onSubmit={(formData) => {
          setRepairShopId(formData.repairShopId);
          setIsOpened(false);
          setIsRepairFormOpened(true);
        }}
        open={isOpened}
        onClose={() => setIsOpened(false)}
        autocompleteData={{ repairShops: repairShopData }}
        loading={repairShopMutation.isPending}
        additionalComponents={
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setIsOpened(false);
              setIsRepairShopOpened(true);
            }}
            key={"addRepairShop"}
          >
            Add Repair Shop
          </Button>
        }
      />
      <GenericFormPopup
        formFields={repairShopFieldConfiguration}
        initialValues={repairShopInitialValues}
        title="Repair Shop Form"
        onSubmit={submitRepairShopHandler}
        open={isRepairShopOpened}
        loading={repairShopMutation.isPending}
        onClose={() => setIsRepairShopOpened(false)}
      />
      <GenericFormPopup
        formFields={repairFieldConfiguration}
        initialValues={repairInitialValues}
        title="Repair Form"
        onSubmit={submitRepairHandler}
        open={isRepairFormOpened}
        onClose={() => setIsRepairFormOpened(false)}
        loading={repairMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage={
          isRepairShopOpened
            ? "Repair shop created successfully"
            : "Repair created successfully"
        }
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default RepairPopup;
